import { useMemo } from 'react';
import {
  IMeasureControl,
  QueryType,
} from '../../../utils/apollo/graphqlSchema';
import { assertCondition } from '../../../utils/types';
import { generateDataTableRows, generateSegments } from './utils';
import { useChartContext } from '../ChartContext';

export function useChartDataTable() {
  const {
    chartData,
    settings: { chartSettings },
    metadata: { comparisonInfo, query },
  } = useChartContext();
  assertCondition(chartData?.queryType === QueryType.DataTable);
  const { measureControls } = chartData;
  const { tableSettings } = chartSettings;
  assertCondition(query?.__typename === 'DataTableQuery');
  const { breakdown, measures } = query;

  const breakdowns = useMemo(() => breakdown?.groupbys ?? [], [breakdown]);
  const segments = useMemo(() => breakdown?.cohortRefs ?? [], [breakdown]);

  const numBreakdowns = breakdowns.length;
  const numSegments = segments.length;

  const hasBreakdown = numBreakdowns > 0;
  const hasSegment = numSegments > 0;
  const hasComparison = !!comparisonInfo;

  const uniqueSegments = useMemo(() => {
    return generateSegments(chartData, segments);
  }, [chartData, segments]);

  // an array of header strings based on measureControls
  // which contain the aliasable measure names
  const headers = useMemo(() => {
    if (measureControls.length) {
      return measureControls.map((measure: IMeasureControl) => measure.name);
    }
    return [];
  }, [measureControls]);

  const rows = useMemo(
    () =>
      generateDataTableRows(
        chartData,
        uniqueSegments,
        hasComparison,
        tableSettings.showPercentDelta,
      ),
    [chartData, uniqueSegments, tableSettings.showPercentDelta, hasComparison],
  );

  return {
    headers,
    rows,
    hasBreakdown,
    hasSegment,
    hasComparison,
    uniqueSegments,
    measures,
    segments,
    breakdowns,
  };
}
